import { Box, LinearProgress, Snackbar, SnackbarContent, Typography } from "@mui/material";
import toast from "react-hot-toast";

interface ReportRefreshBannerProps {
  total?: number | null;
  completed?: number | null;
  startedAt?: string | null;
}

const COMPLETION_TOAST_ID = "report-refresh-completed";

export const showRefreshCompletedToast = () => {
  toast.success(
    "Financial reports refresh completed. Review the updated BS and P&L reports when ready.",
    {
      id: COMPLETION_TOAST_ID,
      duration: 5000,
      position: "top-center"
    }
  );
};

const ReportRefreshBanner = ({
  open,
  total = null,
  completed = null,
  startedAt = null
}: ReportRefreshBannerProps & { open: boolean }) => {
  const safeTotal = total != null && total > 0 ? total : 0;
  const safeCompleted =
    completed != null
      ? Math.min(Math.max(completed, 0), safeTotal || completed)
      : 0;

  const progressPercent =
    safeTotal > 0
      ? Math.min(100, Math.round((safeCompleted / safeTotal) * 100))
      : 15;

  const remaining =
    safeTotal > 0 ? Math.max(safeTotal - safeCompleted, 0) : 0;

  const startedAtTimestamp = startedAt
    ? new Date(startedAt).getTime()
    : null;

  const elapsedMinutes =
    startedAtTimestamp && !Number.isNaN(startedAtTimestamp)
      ? Math.max(0, Math.floor((Date.now() - startedAtTimestamp) / 60000))
      : null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <SnackbarContent
        sx={{
          minWidth: 400,
          bgcolor: "#eff6ff",
          color: "#1f2937",
          border: "1px solid #bfdbfe",
          borderRadius: 2,
          p: 1
        }}
        message={
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#1d4ed8", fontWeight: 700 }}
            >
              Refreshing financial reports in the background
            </Typography>

            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {safeTotal > 0 ? (
                <>
                  {safeCompleted} / {safeTotal} reports completed
                  {remaining > 0 &&
                    ` - ~${Math.ceil(remaining * 0.5)} min remaining`}
                </>
              ) : elapsedMinutes != null ? (
                <>
                  Started{" "}
                  {elapsedMinutes === 0
                    ? "just now"
                    : `${elapsedMinutes} min ago`}
                </>
              ) : (
                <>Preparing updated reports...</>
              )}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                mt: 1.5,
                height: 8,
                borderRadius: 5
              }}
            />
          </Box>
        }
        action={
          <Typography
            variant="body2"
            sx={{
              color: "#1d4ed8",
              fontWeight: 700,
              minWidth: 45
            }}
          >
            {progressPercent}%
          </Typography>
        }
      />
    </Snackbar>
  );
};
export default ReportRefreshBanner;
